import React from "react";
import { Spinner, Text } from "@chakra-ui/react";
import { Calendar, Hash, UserRound } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { TableOutlineDeleteIconButton } from "@/components/ui/table-outline-delete-button";
import { cn } from "@/lib/utils";
import type { Report } from "../../../../types/reports";
import { extractReportCohortSummary, getReportCohortWindowLabel } from "../../../../utils/reportListDisplay";
import {
  extractReportFindingTitles,
  formatReportCardCurrency,
} from "../../../../utils/reportListCardPreview";
import { getReportDisplayTitle } from "../../../../utils/reportWindowLabel";
import {
  REPORTS_STRATEGY_BADGE_CLASS,
  ReportsCardMetaItems,
  ReportsMetaIconChip,
} from "./ReportsCardChrome";
import { reportsListCardClassName } from "../reportsCardShell";

export interface ReportListCardProps {
  report: Report;
  selected: boolean;
  onSelectedChange: (selected: boolean) => void;
  onOpen: (report: Report) => void;
  onDelete: (report: Report) => void;
  deleting?: boolean;
  deleteDisabled?: boolean;
  supplementalTimezone?: string;
  pnlPositiveColor?: string;
  pnlNegativeColor?: string;
}

function formatCreatedLabel(createdAt?: string | null): string {
  if (!createdAt) return "—";
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function ReportBoardCardMeta({
  reportNumber,
  accountCode,
  dateLabel,
}: {
  reportNumber?: number | null;
  accountCode?: string | null;
  dateLabel: string;
}) {
  const items: Array<{ id: string; node: React.ReactNode }> = [];

  if (typeof reportNumber === "number") {
    items.push({
      id: "report",
      node: (
        <ReportsMetaIconChip
          id="report"
          label="Report"
          value={`#${reportNumber}`}
          icon={Hash}
        />
      ),
    });
  }

  if (accountCode?.trim()) {
    items.push({
      id: "account",
      node: (
        <ReportsMetaIconChip
          id="account"
          label="Account"
          value={accountCode.trim()}
          icon={UserRound}
        />
      ),
    });
  }

  if (dateLabel.trim()) {
    items.push({
      id: "date",
      node: (
        <ReportsMetaIconChip
          id="date"
          label="Window"
          value={dateLabel.trim()}
          icon={Calendar}
        />
      ),
    });
  }

  if (items.length === 0) return null;

  return (
    <div data-reports-board-card-meta="" className="mt-1">
      <ReportsCardMetaItems items={items} />
    </div>
  );
}

export function ReportListCard({
  report,
  selected,
  onSelectedChange,
  onOpen,
  onDelete,
  deleting = false,
  deleteDisabled = false,
  pnlPositiveColor = "#06E363",
  pnlNegativeColor = "#E00101",
}: ReportListCardProps): React.ReactElement {
  const title = getReportDisplayTitle(report);
  const windowLabel = getReportCohortWindowLabel(report);
  const rangeStats = extractReportCohortSummary(report);
  const findingTitles = extractReportFindingTitles(report.findings, 2);
  const isProcessing = report.status === "processing";
  const isFailed = report.status === "failed";
  const strategyName = report.strategy?.name?.trim() || null;

  const pnlColor =
    rangeStats && rangeStats.totalPnl >= 0 ? pnlPositiveColor : pnlNegativeColor;

  return (
    <Card
      data-reports-list-card=""
      size="sm"
      className={cn(
        reportsListCardClassName,
        "cursor-pointer",
        isProcessing && "border-[#38BBD1]/60",
        isFailed && "border-red-500/40",
        selected && "border-[#38BBD1] ring-1 ring-[#38BBD1]/30",
      )}
      onClick={() => onOpen(report)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(report);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Open report ${title}`}
    >
      <CardContent className="flex flex-col gap-3 !p-3">
        <div className="flex items-start justify-between gap-2">
          <div
            className="flex min-w-0 flex-1 items-start gap-2"
            onClick={(event) => event.stopPropagation()}
          >
            <Checkbox
              data-reports-list-card-checkbox=""
              checked={selected}
              onCheckedChange={(value) => onSelectedChange(!!value)}
              aria-label={`Select report ${title}`}
              className="mt-0.5 shrink-0"
            />
            <div className="min-w-0 flex-1">
              <Text
                data-reports-accent-text=""
                fontSize="md"
                color="#38BBD1"
                fontFamily="var(--font-primary)"
                fontWeight="medium"
                lineHeight="1.3"
                noOfLines={2}
              >
                {title}
              </Text>
              <ReportBoardCardMeta
                reportNumber={report.report_sequence_number}
                accountCode={report.broker_account_code}
                dateLabel={windowLabel}
              />
              <Text
                data-reports-muted-text=""
                fontSize="xs"
                color="gray.500"
                fontFamily="var(--font-primary)"
                mt={1}
              >
                Created {formatCreatedLabel(report.created_at)}
              </Text>
            </div>
          </div>

          <div
            className="flex shrink-0 flex-col items-end gap-1.5"
            onClick={(event) => event.stopPropagation()}
          >
            {strategyName ? (
              <Badge data-reports-strategy-badge="" className={REPORTS_STRATEGY_BADGE_CLASS}>
                {strategyName}
              </Badge>
            ) : null}
            {isProcessing ? (
              <span className="inline-flex items-center gap-1 rounded border border-cyan-500/40 bg-cyan-500/10 px-1.5 py-0.5 font-[family-name:var(--font-primary)] text-xs text-cyan-700 dark:text-cyan-200">
                <Spinner size="xs" color="#38BBD1" thickness="2px" speed="0.7s" />
                Generating…
              </span>
            ) : null}
            {isFailed ? (
              <Badge
                variant="outline"
                className="border-red-500/40 bg-red-500/10 font-[family-name:var(--font-primary)] text-red-700 dark:text-red-200"
              >
                Failed
              </Badge>
            ) : null}
            <TableOutlineDeleteIconButton
              marker="reports"
              aria-label="Delete report"
              loading={deleting}
              disabled={deleteDisabled}
              onClick={() => onDelete(report)}
            />
          </div>
        </div>

        <div className="min-w-0">
          {rangeStats ? (
            <>
              <Text
                data-trade-log-pnl=""
                fontSize="xl"
                fontWeight="semibold"
                fontFamily="var(--font-primary)"
                lineHeight="1.2"
                style={{ color: pnlColor }}
              >
                {formatReportCardCurrency(rangeStats.totalPnl)}
              </Text>
              <Text
                data-reports-muted-text=""
                fontSize="xs"
                color="gray.500"
                fontFamily="var(--font-primary)"
                mt={0.5}
              >
                {rangeStats.totalTrades} trades · {rangeStats.winRate.toFixed(1)}% win rate
              </Text>
            </>
          ) : (
            <Text
              data-reports-muted-text=""
              fontSize="sm"
              color="gray.500"
              fontFamily="var(--font-primary)"
            >
              Stats unavailable
            </Text>
          )}
        </div>

        {findingTitles.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {findingTitles.map((finding: string) => (
              <Badge
                key={finding}
                variant="outline"
                className="max-w-full truncate border-border bg-transparent font-[family-name:var(--font-primary)] font-normal text-gray-500 dark:text-sidebar-foreground"
                title={finding}
              >
                {finding}
              </Badge>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
